import { Vec2 } from '../utils/math';
import { monochromaticToRGB, rgbToCSS } from '../utils/color';
import { TracedSegment } from '../engine/Ray';
import {
  Component, LaserSource, FlatMirror, Lens, Prism, GlassPlate,
  CurvedMirror, Aperture, BeamSplitter, Detector, DiffractionGrating, Medium, Polarizer,
} from '../engine/Component';
import { Camera } from '../engine/Camera';

export class RayRenderer {
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2D context');
    this.ctx = ctx;
  }

  private dpr: number = 1;

  resize(): void {
    const rect = this.canvas.parentElement?.getBoundingClientRect();
    if (!rect) return;
    this.dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * this.dpr;
    this.canvas.height = rect.height * this.dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  clear(): void {
    // Clear in device pixels (full canvas)
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();
  }

  /** Apply camera transform for world-space rendering */
  applyCamera(camera: Camera, canvasWidth: number, canvasHeight: number): void {
    camera.applyTransform(this.ctx, this.dpr, canvasWidth, canvasHeight);
  }

  /** Reset to screen-space transform */
  resetTransform(): void {
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  drawGrid(camera: Camera, canvasWidth: number, canvasHeight: number, spacing: number): void {
    const topLeft = camera.screenToWorld(new Vec2(0, 0), canvasWidth, canvasHeight);
    const bottomRight = camera.screenToWorld(new Vec2(canvasWidth, canvasHeight), canvasWidth, canvasHeight);
    const startX = Math.floor(topLeft.x / spacing) * spacing;
    const startY = Math.floor(topLeft.y / spacing) * spacing;

    this.ctx.save();
    this.ctx.strokeStyle = 'rgba(100,100,150,0.08)';
    this.ctx.lineWidth = 0.5 / camera.zoom; // stay thin at any zoom

    this.ctx.beginPath();
    for (let x = startX; x <= bottomRight.x; x += spacing) {
      this.ctx.moveTo(x, topLeft.y);
      this.ctx.lineTo(x, bottomRight.y);
    }
    for (let y = startY; y <= bottomRight.y; y += spacing) {
      this.ctx.moveTo(topLeft.x, y);
      this.ctx.lineTo(bottomRight.x, y);
    }
    this.ctx.stroke();
    this.ctx.restore();
  }

  drawRays(segments: TracedSegment[], phaseColorMode: boolean = false): void {
    this.ctx.save();
    this.ctx.globalCompositeOperation = 'lighter';
    this.drawRayPass(segments, 6, 0.15, phaseColorMode);
    this.drawRayPass(segments, 2, 0.7, phaseColorMode);
    this.ctx.restore();
  }

  private drawRayPass(segments: TracedSegment[], lineWidth: number, alpha: number, phaseColor: boolean = false): void {
    for (const seg of segments) {
      const displayIntensity = Math.max(0.15, Math.min(1, seg.intensity));

      let strokeStyle: string;
      if (phaseColor && seg.coherent) {
        // Map phase 0-2π to hue 0-360
        const hue = (((seg.phase % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) * 180 / Math.PI;
        strokeStyle = `hsla(${hue}, 80%, ${50 + displayIntensity * 20}%, ${alpha})`;
      } else {
        const rgb = monochromaticToRGB(seg.wavelength, displayIntensity);
        strokeStyle = rgbToCSS(rgb, alpha);
      }

      this.ctx.beginPath();
      this.ctx.moveTo(seg.start.x, seg.start.y);
      this.ctx.lineTo(seg.end.x, seg.end.y);
      this.ctx.strokeStyle = strokeStyle;
      this.ctx.lineWidth = lineWidth;
      this.ctx.stroke();
    }
  }

  drawSnapIndicator(position: Vec2, hint: string): void {
    this.ctx.save();
    this.ctx.beginPath();
    this.ctx.arc(position.x, position.y, 8, 0, Math.PI * 2);
    this.ctx.strokeStyle = hint === 'brewster_angle' ? '#ffaa44' : '#44aaff';
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([3, 3]);
    this.ctx.stroke();

    const size = 12;
    this.ctx.beginPath();
    this.ctx.moveTo(position.x - size, position.y);
    this.ctx.lineTo(position.x + size, position.y);
    this.ctx.moveTo(position.x, position.y - size);
    this.ctx.lineTo(position.x, position.y + size);
    this.ctx.strokeStyle = 'rgba(100, 180, 255, 0.5)';
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([]);
    this.ctx.stroke();

    this.ctx.font = '10px system-ui';
    this.ctx.fillStyle = 'rgba(100, 180, 255, 0.7)';
    const label = hint === 'brewster_angle' ? 'Brewster' : 'Beam snap';
    this.ctx.fillText(label, position.x + 14, position.y - 4);
    this.ctx.restore();
  }

  drawComponents(components: Component[], selectedId?: number): void {
    for (const comp of components) {
      const isSelected = comp.id === selectedId;

      if (comp instanceof LaserSource) {
        this.drawLaser(comp, isSelected);
      } else if (comp instanceof FlatMirror) {
        this.drawMirror(comp, isSelected);
      } else if (comp instanceof Lens) {
        this.drawLens(comp, isSelected);
      } else if (comp instanceof Prism) {
        this.drawPrism(comp, isSelected);
      } else if (comp instanceof GlassPlate) {
        this.drawGlassPlate(comp, isSelected);
      } else if (comp instanceof CurvedMirror) {
        this.drawCurvedMirror(comp, isSelected);
      } else if (comp instanceof Aperture) {
        this.drawAperture(comp, isSelected);
      } else if (comp instanceof BeamSplitter) {
        this.drawBeamSplitter(comp, isSelected);
      } else if (comp instanceof Detector) {
        this.drawDetector(comp, isSelected);
      } else if (comp instanceof DiffractionGrating) {
        this.drawGrating(comp, isSelected);
      } else if (comp instanceof Medium) {
        this.drawMedium(comp, isSelected);
      } else if (comp instanceof Polarizer) {
        this.drawPolarizer(comp, isSelected);
      } else {
        // Generic fallback: draw segments using component's draw config
        this.drawGenericComponent(comp, isSelected);
      }
    }
  }

  drawComponentGhost(type: string, position: Vec2, angle: number): void {
    this.ctx.globalAlpha = 0.4;
    const ghostColor = '#6666ff';
    switch (type) {
      case 'laser': this.drawLaserShape(position, angle, ghostColor); break;
      case 'mirror': this.drawMirrorShape(position, angle, 80, ghostColor); break;
      case 'lens': this.drawLensShape(position, angle, 60, ghostColor); break;
      case 'prism': this.drawPrismShape(position, angle, 60, ghostColor); break;
      case 'glass': this.drawGlassShape(position, angle, 10, 60, ghostColor); break;
      case 'curved_mirror': this.drawCurvedMirrorShape(position, angle, 150, 80, ghostColor); break;
      case 'aperture': this.drawApertureShape(position, angle, 120, 10, 1, 30, ghostColor); break;
      case 'beam_splitter': this.drawBeamSplitterShape(position, angle, 80, ghostColor); break;
      case 'detector': this.drawDetectorShape(position, angle, 120, ghostColor); break;
      case 'diffraction_grating': this.drawGratingShape(position, angle, 80, ghostColor); break;
      case 'medium': this.drawMediumShape(position, angle, 80, 80, ghostColor); break;
      case 'polarizer': this.drawPolarizerShape(position, angle, 60, 0, ghostColor); break;
    }
    this.ctx.globalAlpha = 1;
  }

  // ─── Laser ───
  private drawLaser(laser: LaserSource, selected: boolean): void {
    const dotColor = laser.whiteLight ? '#ffffff' : rgbToCSS(monochromaticToRGB(laser.wavelength));
    const bodyColor = selected ? '#ff8844' : dotColor;
    this.drawLaserShape(laser.position, laser.angle, bodyColor);
    this.ctx.beginPath();
    this.ctx.arc(laser.position.x, laser.position.y, 3, 0, Math.PI * 2);
    this.ctx.fillStyle = dotColor;
    this.ctx.fill();
  }

  private drawLaserShape(pos: Vec2, angle: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const perp = dir.perpendicular();
    const back = pos.sub(dir.scale(15));
    const halfW = 6;

    this.ctx.beginPath();
    this.ctx.moveTo(back.x + perp.x * halfW, back.y + perp.y * halfW);
    this.ctx.lineTo(pos.x + perp.x * 3, pos.y + perp.y * 3);
    this.ctx.lineTo(pos.x - perp.x * 3, pos.y - perp.y * 3);
    this.ctx.lineTo(back.x - perp.x * halfW, back.y - perp.y * halfW);
    this.ctx.closePath();
    this.ctx.fillStyle = color.replace('rgb', 'rgba').replace(')', ',0.3)');
    this.ctx.fill();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.stroke();
  }

  // ─── Flat Mirror ───
  private drawMirror(mirror: FlatMirror, selected: boolean): void {
    const color = selected ? '#ff8844' : '#c0c0e0';
    this.drawMirrorShape(mirror.position, mirror.angle, mirror.length, color);
  }

  private drawMirrorShape(pos: Vec2, angle: number, length: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const half = dir.scale(length / 2);
    const start = pos.sub(half);
    const end = pos.add(half);

    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    this.ctx.stroke();

    // Hash marks on back side
    const normal = dir.perpendicular();
    for (let i = 0; i <= 5; i++) {
      const t = i / 5;
      const p = start.add(dir.scale(length * t));
      const hashEnd = p.add(normal.scale(-8));
      this.ctx.beginPath();
      this.ctx.moveTo(p.x, p.y);
      this.ctx.lineTo(hashEnd.x, hashEnd.y);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    }
  }

  // ─── Lens ───
  private drawLens(lens: Lens, selected: boolean): void {
    const color = selected ? '#ff8844' : '#88aaff';
    this.drawLensShape(lens.position, lens.angle, lens.aperture, color);
  }

  private drawLensShape(pos: Vec2, angle: number, aperture: number, color: string): void {
    const perp = Vec2.fromAngle(angle).perpendicular();
    const half = perp.scale(aperture / 2);
    const top = pos.add(half);
    const bottom = pos.sub(half);
    const dir = Vec2.fromAngle(angle);
    const bulge = 8;

    this.ctx.beginPath();
    this.ctx.moveTo(top.x, top.y);
    this.ctx.quadraticCurveTo(pos.x + dir.x * bulge, pos.y + dir.y * bulge, bottom.x, bottom.y);
    this.ctx.quadraticCurveTo(pos.x - dir.x * bulge, pos.y - dir.y * bulge, top.x, top.y);
    this.ctx.fillStyle = 'rgba(100, 140, 255, 0.15)';
    this.ctx.fill();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.stroke();

    this.drawArrowTip(top, perp, color);
    this.drawArrowTip(bottom, perp.negate(), color);
  }

  private drawArrowTip(pos: Vec2, direction: Vec2, color: string): void {
    const size = 5;
    const perp = direction.perpendicular().scale(size);
    const tip = pos.add(direction.scale(size));
    this.ctx.beginPath();
    this.ctx.moveTo(pos.x + perp.x, pos.y + perp.y);
    this.ctx.lineTo(tip.x, tip.y);
    this.ctx.lineTo(pos.x - perp.x, pos.y - perp.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  // ─── Prism ───
  private drawPrism(prism: Prism, selected: boolean): void {
    const color = selected ? '#ff8844' : '#88ddaa';
    this.drawPrismShape(prism.position, prism.angle, prism.size, color);
  }

  private drawPrismShape(pos: Vec2, angle: number, size: number, color: string): void {
    const h = size * Math.sqrt(3) / 2;
    const verts = [
      new Vec2(0, -h * 2 / 3),
      new Vec2(-size / 2, h / 3),
      new Vec2(size / 2, h / 3),
    ].map(v => v.rotate(angle).add(pos));

    this.ctx.beginPath();
    this.ctx.moveTo(verts[0].x, verts[0].y);
    this.ctx.lineTo(verts[1].x, verts[1].y);
    this.ctx.lineTo(verts[2].x, verts[2].y);
    this.ctx.closePath();
    this.ctx.fillStyle = 'rgba(100, 200, 150, 0.15)';
    this.ctx.fill();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.stroke();
  }

  // ─── Glass Plate ───
  private drawGlassPlate(plate: GlassPlate, selected: boolean): void {
    const color = selected ? '#ff8844' : '#aaaacc';
    this.drawGlassShape(plate.position, plate.angle, plate.width, plate.height, color);
  }

  private drawGlassShape(pos: Vec2, angle: number, width: number, height: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const perp = dir.perpendicular();
    const hw = dir.scale(width / 2);
    const hh = perp.scale(height / 2);
    const corners = [
      pos.add(hw).add(hh), pos.add(hw).sub(hh),
      pos.sub(hw).sub(hh), pos.sub(hw).add(hh),
    ];

    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.fillStyle = 'rgba(150, 150, 200, 0.15)';
    this.ctx.fill();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.stroke();
  }

  // ─── Curved Mirror ───
  private drawCurvedMirror(mirror: CurvedMirror, selected: boolean): void {
    const color = selected ? '#ff8844' : '#d0d0f0';
    this.drawCurvedMirrorShape(mirror.position, mirror.angle, mirror.radius, mirror.aperture, color);
  }

  private drawCurvedMirrorShape(pos: Vec2, angle: number, radius: number, aperture: number, color: string): void {
    const normal = Vec2.fromAngle(angle).perpendicular();
    const center = pos.add(normal.scale(radius));
    const absR = Math.abs(radius);
    const halfAngle = Math.asin(Math.min(1, aperture / (2 * absR)));
    const baseAngle = Math.atan2(pos.y - center.y, pos.x - center.x);

    // Draw the arc
    const N = 24;
    this.ctx.beginPath();
    for (let i = 0; i <= N; i++) {
      const a = baseAngle - halfAngle + (2 * halfAngle * i) / N;
      const x = center.x + absR * Math.cos(a);
      const y = center.y + absR * Math.sin(a);
      if (i === 0) this.ctx.moveTo(x, y);
      else this.ctx.lineTo(x, y);
    }
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    this.ctx.stroke();

    // Hash marks on back side (opposite to center)
    for (let i = 0; i <= 6; i++) {
      const a = baseAngle - halfAngle + (2 * halfAngle * i) / 6;
      const px = center.x + absR * Math.cos(a);
      const py = center.y + absR * Math.sin(a);
      const outward = new Vec2(Math.cos(a), Math.sin(a));
      // Hash marks go away from center of curvature
      const hashDir = radius > 0 ? outward : outward.negate();
      this.ctx.beginPath();
      this.ctx.moveTo(px, py);
      this.ctx.lineTo(px + hashDir.x * 6, py + hashDir.y * 6);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    }
  }

  // ─── Aperture/Slit ───
  private drawAperture(aperture: Aperture, selected: boolean): void {
    const color = selected ? '#ff8844' : '#b0b0b0';
    this.drawApertureShape(
      aperture.position, aperture.angle, aperture.height,
      aperture.slitWidth, aperture.slitCount, aperture.slitSeparation, color,
    );
  }

  private drawApertureShape(
    pos: Vec2, angle: number, height: number,
    slitWidth: number, slitCount: number, slitSeparation: number, color: string,
  ): void {
    const perp = Vec2.fromAngle(angle).perpendicular();
    const halfH = height / 2;

    // Draw barrier segments (thick solid lines)
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 4;
    this.ctx.lineCap = 'round';

    if (slitCount === 1) {
      const halfSlit = slitWidth / 2;
      const top = pos.add(perp.scale(halfH));
      const slitTop = pos.add(perp.scale(halfSlit));
      const slitBot = pos.sub(perp.scale(halfSlit));
      const bottom = pos.sub(perp.scale(halfH));
      this.ctx.beginPath();
      this.ctx.moveTo(top.x, top.y);
      this.ctx.lineTo(slitTop.x, slitTop.y);
      this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(slitBot.x, slitBot.y);
      this.ctx.lineTo(bottom.x, bottom.y);
      this.ctx.stroke();
    } else {
      const halfSep = slitSeparation / 2;
      const halfSlit = slitWidth / 2;
      const top = pos.add(perp.scale(halfH));
      const s1Top = pos.add(perp.scale(halfSep + halfSlit));
      const s1Bot = pos.add(perp.scale(halfSep - halfSlit));
      const s2Top = pos.sub(perp.scale(halfSep - halfSlit));
      const s2Bot = pos.sub(perp.scale(halfSep + halfSlit));
      const bottom = pos.sub(perp.scale(halfH));

      this.ctx.beginPath();
      this.ctx.moveTo(top.x, top.y); this.ctx.lineTo(s1Top.x, s1Top.y); this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(s1Bot.x, s1Bot.y); this.ctx.lineTo(s2Top.x, s2Top.y); this.ctx.stroke();
      this.ctx.beginPath();
      this.ctx.moveTo(s2Bot.x, s2Bot.y); this.ctx.lineTo(bottom.x, bottom.y); this.ctx.stroke();
    }

    this.ctx.lineCap = 'butt';
  }

  // ─── Beam Splitter ───
  private drawBeamSplitter(splitter: BeamSplitter, selected: boolean): void {
    const color = selected ? '#ff8844' : '#99bbdd';
    this.drawBeamSplitterShape(splitter.position, splitter.angle, splitter.length, color);
  }

  private drawBeamSplitterShape(pos: Vec2, angle: number, length: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const half = dir.scale(length / 2);
    const start = pos.sub(half);
    const end = pos.add(half);

    // Dashed line to indicate partial transparency
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2.5;
    this.ctx.setLineDash([6, 4]);
    this.ctx.stroke();
    this.ctx.setLineDash([]);

    // Small circles at ends
    for (const p of [start, end]) {
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      this.ctx.fillStyle = color;
      this.ctx.fill();
    }
  }

  // ─── Detector/Screen ───
  private drawDetector(detector: Detector, selected: boolean): void {
    const color = selected ? '#ff8844' : '#ddaa44';
    this.drawDetectorShape(detector.position, detector.angle, detector.length, color);

    // Draw intensity profile graph
    const maxI = detector.getMaxIntensity();
    if (maxI > 0) {
      this.drawIntensityProfile(detector, color);
    }
  }

  private drawDetectorShape(pos: Vec2, angle: number, length: number, color: string): void {
    const perp = Vec2.fromAngle(angle).perpendicular();
    const half = perp.scale(length / 2);
    const start = pos.sub(half);
    const end = pos.add(half);

    // Thick line for the detector surface
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 4;
    this.ctx.stroke();

    // Small tick marks along the surface
    const dir = Vec2.fromAngle(angle);
    for (let i = 0; i <= 4; i++) {
      const t = i / 4;
      const p = start.add(end.sub(start).scale(t));
      const tickEnd = p.add(dir.scale(-6));
      this.ctx.beginPath();
      this.ctx.moveTo(p.x, p.y);
      this.ctx.lineTo(tickEnd.x, tickEnd.y);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    }
  }

  private drawIntensityProfile(detector: Detector, color: string): void {
    const surface = detector.getSurface();
    const surfaceDir = surface.end.sub(surface.start);
    const normal = Vec2.fromAngle(detector.angle);
    const maxI = detector.getMaxIntensity();
    if (maxI < 1e-10) return;

    const graphHeight = 40; // max pixel height of the graph
    const bins = detector.bins;

    this.ctx.beginPath();
    this.ctx.moveTo(surface.start.x, surface.start.y);

    for (let i = 0; i < bins.length; i++) {
      const t = (i + 0.5) / bins.length;
      const basePoint = surface.start.add(surfaceDir.scale(t));
      const height = (detector.getComputedIntensity(i) / maxI) * graphHeight;
      const graphPoint = basePoint.add(normal.scale(-height));

      if (i === 0) {
        this.ctx.moveTo(graphPoint.x, graphPoint.y);
      } else {
        this.ctx.lineTo(graphPoint.x, graphPoint.y);
      }
    }

    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.globalAlpha = 0.8;
    this.ctx.stroke();
    this.ctx.globalAlpha = 1;
  }

  // ─── Diffraction Grating ───
  private drawGrating(grating: DiffractionGrating, selected: boolean): void {
    const color = selected ? '#ff8844' : '#bb88ee';
    this.drawGratingShape(grating.position, grating.angle, grating.length, color);
  }

  private drawGratingShape(pos: Vec2, angle: number, length: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const half = dir.scale(length / 2);
    const start = pos.sub(half);
    const end = pos.add(half);
    const normal = dir.perpendicular();

    // Main line
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2;
    this.ctx.stroke();

    // Ruled lines (perpendicular ticks to suggest grating pattern)
    const rulingCount = 10;
    const tickLen = 6;
    for (let i = 0; i <= rulingCount; i++) {
      const t = i / rulingCount;
      const p = start.add(dir.scale(length * t));
      this.ctx.beginPath();
      this.ctx.moveTo(p.x - normal.x * tickLen, p.y - normal.y * tickLen);
      this.ctx.lineTo(p.x + normal.x * tickLen, p.y + normal.y * tickLen);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 0.8;
      this.ctx.stroke();
    }
  }

  // ─── Medium ───
  private drawMedium(medium: Medium, selected: boolean): void {
    const color = selected ? '#ff8844' : '#887766';
    this.drawMediumShape(medium.position, medium.angle, medium.width, medium.height, color);
  }

  private drawMediumShape(pos: Vec2, angle: number, width: number, height: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const perp = dir.perpendicular();
    const hw = dir.scale(width / 2);
    const hh = perp.scale(height / 2);
    const corners = [
      pos.add(hw).add(hh), pos.add(hw).sub(hh),
      pos.sub(hw).sub(hh), pos.sub(hw).add(hh),
    ];

    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.fillStyle = 'rgba(100, 80, 60, 0.25)';
    this.ctx.fill();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1;
    this.ctx.setLineDash([4, 3]);
    this.ctx.stroke();
    this.ctx.setLineDash([]);
  }

  // ─── Polarizer ───
  private drawPolarizer(polarizer: Polarizer, selected: boolean): void {
    const color = selected ? '#ff8844' : '#cccc66';
    this.drawPolarizerShape(polarizer.position, polarizer.angle, polarizer.length, polarizer.transmissionAxis, color);
  }

  private drawPolarizerShape(pos: Vec2, angle: number, length: number, axisAngle: number, color: string): void {
    const dir = Vec2.fromAngle(angle);
    const half = dir.scale(length / 2);
    const start = pos.sub(half);
    const end = pos.add(half);

    // Draw the polarizer surface
    this.ctx.beginPath();
    this.ctx.moveTo(start.x, start.y);
    this.ctx.lineTo(end.x, end.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    this.ctx.stroke();

    // Draw transmission axis arrow through center
    const axisDir = Vec2.fromAngle(angle + axisAngle);
    const arrowLen = length * 0.35;
    const arrowStart = pos.sub(axisDir.scale(arrowLen));
    const arrowEnd = pos.add(axisDir.scale(arrowLen));

    this.ctx.beginPath();
    this.ctx.moveTo(arrowStart.x, arrowStart.y);
    this.ctx.lineTo(arrowEnd.x, arrowEnd.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 1.5;
    this.ctx.setLineDash([]);
    this.ctx.stroke();

    // Arrowhead
    const headSize = 5;
    const headPerp = axisDir.perpendicular().scale(headSize);
    const headBack = arrowEnd.sub(axisDir.scale(headSize * 1.5));
    this.ctx.beginPath();
    this.ctx.moveTo(arrowEnd.x, arrowEnd.y);
    this.ctx.lineTo(headBack.x + headPerp.x, headBack.y + headPerp.y);
    this.ctx.moveTo(arrowEnd.x, arrowEnd.y);
    this.ctx.lineTo(headBack.x - headPerp.x, headBack.y - headPerp.y);
    this.ctx.stroke();
  }

  // ─── Generic fallback ───
  private drawGenericComponent(comp: Component, selected: boolean): void {
    const config = comp.getDrawConfig();
    const color = selected ? config.selectedStrokeColor : config.strokeColor;
    const segments = comp.getSegments();

    this.ctx.beginPath();
    for (const seg of segments) {
      this.ctx.moveTo(seg.start.x, seg.start.y);
      this.ctx.lineTo(seg.end.x, seg.end.y);
    }
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2;
    this.ctx.stroke();
  }

  getCanvasSize(): { width: number; height: number } {
    const rect = this.canvas.parentElement?.getBoundingClientRect();
    return { width: rect?.width ?? 800, height: rect?.height ?? 600 };
  }
}
